import type {
  Code,
  Effects,
  Extension as MicromarkExtension,
  State,
  TokenizeContext,
  TokenType,
  Token,
} from "micromark-util-types";
import type { CompileContext, Extension as FromMarkdownExtension } from "mdast-util-from-markdown";
import type { Content, PhrasingContent } from "mdast";
import type { Processor } from "unified";

interface InlineRule {
  tag: string;
  type: string;
}

function createSyntaxExtension(rules: InlineRule[]): MicromarkExtension {
  const text: MicromarkExtension["text"] = {};

  for (const rule of rules) {
    const { type } = rule;
    const charCode = rule.tag.charCodeAt(0);
    const tagLength = rule.tag.length;
    const markerType = `${type}Marker` as TokenType;
    const dataType = `${type}Data` as TokenType;
    const tokenType = type as TokenType;

    text[charCode] = {
      name: type,
      tokenize(this: TokenizeContext, effects: Effects, ok: State, nok: State): State {
        let openCount = 0;
        let closeCount = 0;

        return start;

        function start(code: Code): State | void {
          effects.enter(tokenType);
          effects.enter(markerType);
          return opening(code);
        }

        function opening(code: Code): State | void {
          if (code === charCode) {
            effects.consume(code);
            openCount++;
            if (openCount === tagLength) {
              effects.exit(markerType);
              return beforeContent;
            }
            return opening;
          }
          return nok(code);
        }

        function beforeContent(code: Code): State | void {
          // Reject empty content, line endings, EOF, and immediate closing delimiter
          if (code === null || code === 10 || code === 13 || code === charCode) {
            return nok(code);
          }
          effects.enter(dataType);
          return content(code);
        }

        function content(code: Code): State | void {
          if (code === null || code === 10 || code === 13) {
            return nok(code);
          }
          if (code === charCode) {
            effects.exit(dataType);
            effects.enter(markerType);
            closeCount = 0;
            return closing(code);
          }
          effects.consume(code);
          return content;
        }

        function closing(code: Code): State | void {
          if (code === charCode && closeCount < tagLength) {
            effects.consume(code);
            closeCount++;
            return closing;
          }
          if (closeCount === tagLength) {
            effects.exit(markerType);
            effects.exit(tokenType);
            return ok(code);
          }
          return nok(code);
        }
      },
    };
  }

  return { text };
}

function createFromMarkdownExtension(rules: InlineRule[]): FromMarkdownExtension {
  const enter: FromMarkdownExtension["enter"] = {};
  const exit: FromMarkdownExtension["exit"] = {};

  for (const { type } of rules) {
    const dataType = `${type}Data`;
    const nodeType = type as PhrasingContent["type"];
    enter![type] = function (this: CompileContext, token: Token) {
      this.enter({ type: nodeType, children: [] } as Content, token);
    };
    enter![dataType] = function (this: CompileContext, token: Token) {
      this.config.enter["data"].call(this, token);
    };
    exit![dataType] = function (this: CompileContext, token: Token) {
      this.config.exit["data"].call(this, token);
    };
    exit![type] = function (this: CompileContext, token: Token) {
      this.exit(token);
    };
  }

  return { enter, exit };
}

type ProcessorData = {
  micromarkExtensions?: MicromarkExtension[];
  fromMarkdownExtensions?: FromMarkdownExtension[];
};

export function remarkInline(rules: InlineRule[]) {
  return function (this: Processor) {
    const data = this.data() as ProcessorData;
    (data.micromarkExtensions ??= []).push(createSyntaxExtension(rules));
    (data.fromMarkdownExtensions ??= []).push(createFromMarkdownExtension(rules));
  };
}
